// FroggyChatBubble — global floating chat widget (P-06).
//
// Mounts site-wide via App.jsx. Collapsed = round button bottom-right;
// expanded = chat modal pinned bottom-right with streaming SSE chat.
// Works for anonymous AND logged-in visitors (the streaming endpoint is
// `permission_classes = [AllowAny]`; anonymous sessions are tracked
// server-side via Django session cookies).
//
// Minimum-viable v1: standalone component, self-contained streaming.
// Markdown rendering + host whitelist intentionally DUPLICATED from
// FroggyChatPageStreaming.jsx — Cowork QA validated those rules at the
// page level and we want byte-identical behavior here. After v1 stabilizes,
// extract to a shared `src/lib/froggyChat.js`. Don't deviate from the
// page's rules without re-running QA.
//
// The waiting indicator (FroggyWorking.jsx) is SHARED with the page
// (operator ruling 2026-10-10).
//
// Features NOT in v1 (port from FroggyChatPageStreaming later if needed):
//   - Heartbeat hint after silent gaps > 8s
//   - Conversation persistence beyond the session
//
// Added 2026-05-26 (post-launch polish per operator review):
//   - Feeling Froggy mascot logo in collapsed button + expanded header
//     (replaces generic 🐸 emoji)
//   - Talk to a Human button → opens shared EscalateToSupportModal
//   - Voice input button → reuses VoiceRecorder + sendFroggyVoiceMessage

import { useContext, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { nanoid } from 'nanoid';

import { AuthContext } from '../../context/AuthContext';
import { useActiveFacilityId } from '../../hooks/useAppBootstrap';
import { streamingAPI } from '../../services/streamingAPI';
import EscalateToSupportModal from '../EscalateToSupportModal';
import VoiceRecorder from '../froggerAI/VoiceRecorder';
import { sendFroggyVoiceMessage, getFroggyVoiceConfig } from '../../aiApi';
import { scrollIntoViewMotionSafe } from '../../utils/scrollHelpers';
import { MARKDOWN_COMPONENTS } from './chatMarkdown';
import FroggyWorking from './FroggyWorking';

// ────────────────────────────────────────────────────────────────────
// Shared rendering rules — `chatMarkdown.jsx`, lifted out of this file
// byte for byte so the FroggerAI dock renders an answer the SAME way
// (Cowork run 24, finding 8). Cowork QA (Runs 31–34, 2026-05-25)
// validated those rules. Image-host whitelist matters for security
// (HIGH-S1 from 2026-05-23). FroggyChatPageStreaming.jsx still keeps its
// own duplicate.
// ────────────────────────────────────────────────────────────────────

// ────────────────────────────────────────────────────────────────────
// Locate-by-id message updater. Adversarial review HIGH-1 / MED-5 —
// array-index addressing is unsafe under concurrent state updates.
// ────────────────────────────────────────────────────────────────────
function updateMessageById(messages, id, patch) {
  const i = messages.findIndex((m) => m && m.id === id);
  if (i < 0) return messages;
  const copy = [...messages];
  copy[i] = { ...copy[i], ...patch };
  return copy;
}


// ────────────────────────────────────────────────────────────────────
// THE FOOTER RESERVES THE PILL'S SPACE (Cowork run 39, batch 41).
//
// Run 38 (H3b) found the pill sitting on the footer's "FAQ" link, and
// batch 40 LIFTED the pill above a visible footer. Run 39 then found the
// lifted pill covering the end of the last content card above the footer
// (Featured Deals at 1280, and at 600px tall) - a lift moves the pill onto
// whatever is above the footer instead. So the pill no longer moves: like
// the FroggerAI dock (`--ff-dock-height`, index.css), it RESERVES its own
// space. While the bubble is mounted and collapsed it publishes its
// measured footprint - the pill's height plus its fixed bottom offset, i.e.
// how far up from the window's bottom edge it reaches - as
// `--froggy-pill-height` on <html>, and `.ff-footer` pads its bottom by
// that plus a 16px margin (the style block below). At the end of the scroll
// the footer's link row therefore sits ABOVE the pill and nothing is lifted
// over content. Mid-scroll overlap is transient and accepted for a floating
// button. `0px` while the chat is open and on unmount, so a page the pill
// is not on reserves nothing.
// ────────────────────────────────────────────────────────────────────
export const PILL_BOTTOM_PX = 24;
export const PILL_RESERVE_VAR = '--froggy-pill-height';
export const PILL_RESERVE_MARGIN_PX = 16;

// Any storefront element may OPEN the bubble, exactly as the pill does, by
// dispatching this window event (operator, 2026-10-09: the home page's
// "Meet FroggyAI" card "ought to open it up just like the pill does").
export const FROGGY_OPEN_EVENT = 'froggyai:open';
export function openFroggyChat() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(FROGGY_OPEN_EVENT));
}

/** How far up from the window's bottom edge the collapsed pill reaches. */
export function pillFootprint(el) {
  if (!el || typeof el.getBoundingClientRect !== 'function') return 0;
  const h = el.getBoundingClientRect().height || 0;
  return h > 0 ? Math.ceil(h) + PILL_BOTTOM_PX : 0;
}

function useReserveThePillsSpace(buttonRef, isOpen) {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const publish = (px) => root.style.setProperty(PILL_RESERVE_VAR, `${px}px`);
    // An OPEN chat is a panel, not a pill: nothing to reserve.
    if (isOpen) {
      publish(0);
      return undefined;
    }
    const measure = () => publish(pillFootprint(buttonRef.current));
    measure();
    let observer = null;
    if (typeof ResizeObserver !== 'undefined' && buttonRef.current) {
      observer = new ResizeObserver(measure);
      observer.observe(buttonRef.current);
    }
    return () => {
      if (observer) observer.disconnect();
      publish(0);
    };
  }, [buttonRef, isOpen]);
}

// ────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────
export default function FroggyChatBubble() {
  const { isAuthenticated } = useContext(AuthContext) || { isAuthenticated: false };

  // Read the facility ID from the SAME hook the rest of the app uses
  // (useAppBootstrap publishes it via the appBootstrap event bus).
  // Cowork QA Run 35 caught the prior bubble doing its own
  // `/api/store/homepage/` fetch which was failing in the deployed
  // env even though `useAppBootstrap` had already resolved a value.
  const activeFacilityId = useActiveFacilityId();

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [showEscalate, setShowEscalate] = useState(false);

  // Voice input — same flow as the full-page chat. Config fetched
  // lazily when the panel first opens; null until then.
  const [voiceConfig, setVoiceConfig] = useState(null);
  const [isVoiceLoading, setIsVoiceLoading] = useState(false);

  const messagesEndRef = useRef(null);
  const controllerRef = useRef(null);
  const pillRef = useRef(null);
  useReserveThePillsSpace(pillRef, isOpen);
  // ⚠️ ONE CONVERSATION ID PER CONVERSATION (Cowork run 38, H1). Sent with
  // every question; "New chat" mints a new one, so the server starts the
  // next conversation EMPTY instead of replaying the one the customer just
  // cleared off the screen. Minted per mount too: the transcript is not
  // kept across a reload, so neither is the server's memory of it.
  const conversationIdRef = useRef(null);
  if (conversationIdRef.current === null) conversationIdRef.current = nanoid();

  // Auto-scroll to latest message on update.
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      scrollIntoViewMotionSafe(messagesEndRef.current);
    }
  }, [messages, isOpen]);

  // Opened from elsewhere on the page (the home page's FroggyAI card).
  // Opening only — never a toggle, so a second click cannot close it.
  // ⚠️ AND IT PUTS THE CURSOR IN THE COMPOSER (Cowork run 40). The card is
  // far from the panel, so a keyboard user who pressed Enter on it was left
  // on the card with the chat open in the corner and no way to reach it but
  // tabbing through the page. The event asks for focus; the effect below
  // gives it once the panel (and its input) is on the page. The PILL keeps
  // its own focus — it sits beside the panel and is how the panel closes.
  const inputRef = useRef(null);
  const [focusRequest, setFocusRequest] = useState(0);
  useEffect(() => {
    const open = () => {
      setIsOpen(true);
      setFocusRequest((n) => n + 1);
    };
    window.addEventListener(FROGGY_OPEN_EVENT, open);
    return () => window.removeEventListener(FROGGY_OPEN_EVENT, open);
  }, []);
  useEffect(() => {
    if (focusRequest && inputRef.current) inputRef.current.focus();
  }, [focusRequest]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        try { controllerRef.current.abort(); } catch (_) { /* noop */ }
      }
    };
  }, []);

  // Fetch voice config the first time the panel opens. Idempotent —
  // only refetches if the prior config call failed (voiceConfig stays
  // null on error and we retry on next open).
  useEffect(() => {
    if (!isOpen || voiceConfig) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await getFroggyVoiceConfig();
        if (!cancelled && r && r.data) {
          setVoiceConfig(r.data);
        }
      } catch (e) {
        // Voice is optional — if config fails, the button just won't
        // render. Don't surface the error to the customer.
        console.warn('[FroggyChatBubble] voice config fetch failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, voiceConfig]);

  const handleToggle = () => {
    setIsOpen((open) => !open);
  };

  // Core "send a question through SSE" path. Used by both text
  // submit AND voice transcript. Extracted 2026-05-26 so the voice
  // handler can feed transcribed audio through the same streaming +
  // rendering pipeline rather than duplicating it.
  const askQuestion = async (question) => {
    if (!question || isStreaming) return;
    setError(null);

    // facilityId is nullable — the backend FroggyAdvisorStreamView has
    // permission_classes=[AllowAny] and streamingAPI.streamFroggyAI
    // handles null by hitting the no-facility URL variant
    // (/api/facility/advisor/froggy/ask/stream, no <uuid> path segment).
    // This is required for the P-06 public-mode chat bubble to work
    // for anonymous/incognito visitors who have no facility context.
    // The prior "Loading facility information — try again in a moment"
    // guard here made the bubble unusable in incognito (2026-07-01
    // operator hand-test); the message was a stale defense against a
    // logged-in bootstrap race that never actually reached this code
    // path anyway.
    const facilityId = activeFacilityId;

    // Push the user message + an assistant placeholder. The
    // placeholder id is captured in the closure below and used to
    // locate the row on every stream update — NOT a numeric index.
    // Array-index addressing is fragile here because any concurrent
    // setMessages call (e.g., from a route change forcing re-mount,
    // or future external state appended to the list) shifts the
    // index out from under us. Adversarial review HIGH-1/MED-5
    // (2026-05-25).
    const userMsg = { id: nanoid(), role: 'user', content: question };
    const placeholder = {
      id: nanoid(),
      role: 'assistant',
      content: '',
      streaming: true,
      streamingStatus: null,
    };
    const placeholderId = placeholder.id;
    setMessages((m) => [...m, userMsg, placeholder]);
    setIsStreaming(true);

    // Use the shared streamingAPI service (same module the full-page
    // chat uses). It handles:
    //   - The correct API_BASE_URL (env-driven; beta uses
    //     api-beta.feelingfroggy.llc on a separate subdomain, NOT
    //     the same origin as the React app — Cowork QA Run 37 found
    //     the hand-rolled fetch hitting the React origin and getting
    //     HTML back).
    //   - X-Facility-Id header
    //   - credentials: 'include' for Django session cookies
    //   - SSE framing + parse
    //   - Strict-mode env flag for diagnostic mode
    let buf = '';

    // ⚠️ A STOPPED TURN IS OVER (review of the polish batch). The service's
    // returned promise only settles when the stream ENDS, so the old
    // `streamHandle.close` check never found a handle and controllerRef was
    // always null: "New chat" mid-turn and unmount closed nothing. The
    // handle arrives through `onOpen` before the first byte; aborting this
    // turn's controller closes it. Every callback checks `stopped()` first,
    // so a late chunk from the old turn cannot touch the new conversation
    // (its error banner, its isStreaming flag).
    const turn = new AbortController();
    const stopped = () => turn.signal.aborted;
    const onOpen = (handle) => {
      if (!handle || typeof handle.close !== 'function') return;
      if (stopped()) {
        handle.close();
        return;
      }
      turn.signal.addEventListener('abort', () => handle.close(), { once: true });
    };
    const release = () => {
      if (controllerRef.current === turn) controllerRef.current = null;
    };
    // ONE ending per turn. `onComplete` fires on `done`; a stream that just
    // stops (no `done`, no error) fires nothing, and used to leave the input
    // disabled until "New chat" — so the turn is also ended when the
    // service's promise settles, which means the stream is over (the
    // reconnect stays inside it). Whichever comes first ends it; the other
    // is a no-op, and neither touches a turn that was stopped.
    let ended = false;
    const finishTurn = () => {
      if (stopped() || ended) return;
      ended = true;
      release();
      setMessages((m) => updateMessageById(m, placeholderId, {
        streaming: false,
        streamingStatus: null,
        content: (buf || '').trim() ||
          'Sorry, I didn\'t catch that — try asking again?',
      }));
      setIsStreaming(false);
    };
    const failTurn = () => {
      if (stopped() || ended) return;
      ended = true;
      release();
      setError('Connection issue — please try again.');
      setMessages((m) => updateMessageById(m, placeholderId, {
        streaming: false,
        streamingStatus: null,
        content: buf || 'Sorry, something went wrong. Please try again.',
        error: true,
      }));
      setIsStreaming(false);
    };
    controllerRef.current = turn;
    try {
      const over = streamingAPI.streamFroggyAI(
        facilityId,
        question,
        // onMessage — the wire payload is { type, text|content|message }
        (payload) => {
          if (stopped()) return;
          if (!payload || typeof payload !== 'object') return;
          if (payload.type === 'status') {
            const statusText = payload.text || '';
            setMessages((m) => updateMessageById(m, placeholderId, {
              streamingStatus: statusText,
            }));
          } else if (payload.type === 'delta') {
            buf += payload.text ?? payload.content ?? '';
            setMessages((m) => updateMessageById(m, placeholderId, {
              content: buf,
              streamingStatus: null,
            }));
          } else if (payload.type === 'error') {
            setError('Connection issue — please try again.');
            setMessages((m) => updateMessageById(m, placeholderId, {
              streaming: false,
              streamingStatus: null,
              content: buf || 'Sorry, something went wrong. Please try again.',
              error: true,
            }));
          }
        },
        // onError
        () => failTurn(),
        // onComplete
        () => finishTurn(),
        null,
        conversationIdRef.current,
        onOpen,
      );
      Promise.resolve(over).then(finishTurn, failTurn);
    } catch (err) {
      failTurn();
    }
  };

  // Text-input submit. Drains the draft and feeds it through askQuestion.
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const question = draft.trim();
    if (!question || isStreaming) return;
    setDraft('');
    await askQuestion(question);
  };

  // Voice submit. Same flow as the full-page chat: send audio →
  // get transcript → feed transcript through askQuestion.
  const handleVoiceMessage = async (audioBlob, duration) => {
    if (isStreaming || isVoiceLoading) return;
    setIsVoiceLoading(true);
    setError(null);
    try {
      const facilityId = activeFacilityId;
      const r = await sendFroggyVoiceMessage(facilityId, audioBlob, duration);
      const transcript = r?.data?.transcript;
      if (!transcript || !transcript.trim()) {
        setError('Could not transcribe audio. Please try again.');
        return;
      }
      setIsVoiceLoading(false);
      await askQuestion(transcript.trim());
    } catch (err) {
      const msg = err?.response?.data?.details
        || err?.message
        || 'Voice message failed';
      setError(msg);
    } finally {
      setIsVoiceLoading(false);
    }
  };

  const handleClear = () => {
    if (controllerRef.current) {
      try { controllerRef.current.abort(); } catch (_) { /* noop */ }
      controllerRef.current = null;
    }
    setMessages([]);
    setDraft('');
    setError(null);
    setIsStreaming(false);
    // A NEW CONVERSATION ON THE SERVER TOO (run 38, H1): the next question
    // goes out under a new id, so nothing said before it is replayed.
    conversationIdRef.current = nanoid();
  };

  return (
    <>
      {/* Collapsed-state bubble button. Always rendered; visibility
          flipped via CSS so the modal slides over it. */}
      <button
        type="button"
        ref={pillRef}
        className={`froggy-bubble-btn ${isOpen ? 'is-open' : ''}`}
        onClick={handleToggle}
        aria-label={isOpen ? 'Close FroggyAI chat' : 'Open FroggyAI chat'}
        aria-expanded={isOpen}
      >
        {/* Closed state: pill with frog mascot + "FroggyAI" wordmark.
            Pill shape (not a circle) distinguishes it from the home/
            logo button in the top-left navbar, which is also a frog.
            Background is Froggy Blue with black text since R5 B5 (it was
            Froggy Orange, white text at 3.1:1). The mascot uses the
            same ff_original asset the home button uses — we know
            its colors render reliably — and we force a thick white
            disc behind it so it pops against the pill. */}
        {/* Cowork QA 2026-05-27 found the wrap was being hidden by a
            global CSS rule `[aria-hidden="true"] { visibility: hidden }`
            (misuse of aria-hidden — should be for screen-reader
            silencing only, not visual hide). Wrap has no aria-hidden
            now: the parent <button> already has aria-label so screen
            readers get the intent; the visible inner content doesn't
            need to be re-flagged. The decorative emoji itself stays
            aria-hidden so the SR doesn't announce "frog emoji" before
            the button label. */}
        <div
          className="froggy-bubble-btn-logo-wrap"
          style={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            background: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '0 0 52px',
            overflow: 'hidden',
            boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
            fontSize: '28px',
            lineHeight: 1,
          }}
        >
          <img
            src="/logos/ff original 500px.png"
            alt=""
            style={{
              width: 48,
              height: 48,
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </div>
        <span className="froggy-bubble-btn-label">
          {isOpen ? '× Close' : 'Ask FroggyAI'}
        </span>
      </button>

      {/* Expanded chat panel. Conditionally mounted so its event
          listeners and scroll position don't accrue when closed. */}
      {isOpen && (
        <div
          className="froggy-bubble-panel"
          role="dialog"
          aria-label="FroggyAI chat"
        >
          <header className="froggy-bubble-header">
            <div className="froggy-bubble-title">
              <img
                src="/logos/ff original 500px.png"
                alt=""
                className="froggy-bubble-header-logo"
                aria-hidden="true"
              />
              <span>Ask FroggyAI</span>
            </div>
            <div className="froggy-bubble-header-actions">
              {messages.length > 0 && (
                <button
                  type="button"
                  className="froggy-bubble-link-btn"
                  onClick={handleClear}
                  disabled={isStreaming && messages.length === 0}
                >
                  New chat
                </button>
              )}
              <button
                type="button"
                className="froggy-bubble-link-btn"
                onClick={handleToggle}
                aria-label="Close chat"
              >
                ×
              </button>
            </div>
          </header>

          <div className="froggy-bubble-messages" role="log" aria-live="polite">
            {messages.length === 0 && (
              <div className="froggy-bubble-welcome">
                <p>
                  <strong>Hi! I'm FroggyAI</strong>{' '}
                  <img
                    src="/logos/ff_original_96px.png"
                    alt=""
                    className="froggy-welcome-logo"
                    data-froggy-welcome-logo
                    style={{ height: '1.25em', width: '1.25em', verticalAlign: 'middle' }}
                  />
                </p>
                <p>
                  Ask about a species, care, shipping, or anything about
                  Feeling Froggy.
                </p>
                <p className="froggy-bubble-welcome-scope">
                  <span aria-hidden="true">💡</span>{' '}
                  <em>I can help with care for <strong>any frog,
                  amphibian, or reptile</strong> — not just what we
                  carry.</em>
                </p>
                <p className="froggy-bubble-welcome-hint">
                  Want a real person? Click <strong>Talk to a human</strong> any time.
                </p>
              </div>
            )}
            {messages.map((m) => (
              <div
                key={m.id}
                className={`froggy-bubble-msg froggy-bubble-msg--${m.role}${m.error ? ' froggy-bubble-msg--error' : ''}`}
              >
                {m.role === 'assistant' ? (
                  <>
                    {m.streaming && !m.content ? (
                      <FroggyWorking />
                    ) : (
                      <ReactMarkdown components={MARKDOWN_COMPONENTS}>
                        {m.content || ''}
                      </ReactMarkdown>
                    )}
                    {m.streaming && m.streamingStatus && (
                      <div className="froggy-bubble-status" role="status">
                        {m.streamingStatus}
                      </div>
                    )}
                  </>
                ) : (
                  m.content
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {error && (
            <div className="froggy-bubble-error" role="alert">
              {error}
            </div>
          )}

          {/* Action row: Talk to a Human + Voice (if config available).
              Always visible — customers should never have to hunt for
              the escalate path. Same wiring as the full-page chat. */}
          <div className="froggy-bubble-actions">
            <button
              type="button"
              className="ff-btn ff-btn--secondary ff-btn--sm froggy-bubble-action-btn--escalate"
              onClick={() => setShowEscalate(true)}
              disabled={isStreaming || isVoiceLoading}
            >
              Talk to a human
            </button>
            {voiceConfig && (
              <div className="froggy-bubble-voice-wrap">
                <VoiceRecorder
                  onVoiceMessage={handleVoiceMessage}
                  disabled={isStreaming || isVoiceLoading}
                  maxDurationSeconds={voiceConfig.max_duration_seconds}
                  recommendedMaxSeconds={voiceConfig.recommended_max_seconds}
                  variant="secondary"
                />
              </div>
            )}
          </div>

          <form className="froggy-bubble-input" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={
                isVoiceLoading
                  ? 'Transcribing voice…'
                  : isStreaming
                    ? 'FroggyAI is typing…'
                    : 'Ask something…'
              }
              disabled={isStreaming || isVoiceLoading}
              aria-label="Message FroggyAI"
              maxLength={2000}
            />
            {/* Batch 34 C4: Send is the bubble's ONE primary, in the family
                and at the composer's height (36px, the input's own). It was
                a pale-cyan 43px 6px-radius button of its own. */}
            <button
              type="submit"
              className="ff-btn ff-btn--primary froggy-bubble-send"
              disabled={isStreaming || isVoiceLoading || !draft.trim()}
              aria-label="Send"
            >
              Send
            </button>
          </form>
        </div>
      )}

      {/* Escalate modal — shared component used by the full-page chat
          too. Operator support form. */}
      <EscalateToSupportModal
        open={showEscalate}
        onClose={() => setShowEscalate(false)}
        prefillNotes={
          messages.length > 0
            ? `From FroggyChatBubble. Last conversation:\n\n` +
              messages.slice(-6).map((m) =>
                `${m.role === 'user' ? 'Customer' : 'FroggyAI'}: ${(m.content || '').slice(0, 500)}`
              ).join('\n\n')
            : ''
        }
      />

      {/* Scoped styles. Inline CSS-in-JSX (not styled-components) to
          match the project's existing CSS-in-JS pattern from
          FroggyChatPageStreaming.jsx. */}
      <style>{`
        .froggy-bubble-btn {
          position: fixed;
          /* Never lifted: the FOOTER reserves this pill's space instead
             (run 39 - see useReserveThePillsSpace). Keep in step with
             PILL_BOTTOM_PX. */
          bottom: 24px;
          right: 24px;
          /* Pill shape — taller than a typical button, height auto-
             sized by the logo. Width auto-fits the FroggyAI label.
             The horizontal silhouette differentiates it from the
             circular home/logo button in the top-left navbar. */
          height: 56px;
          padding: 4px 18px 4px 4px;
          gap: 10px;
          border-radius: 28px;
          /* R5 B5 (desktop standard, 2026-10-26): Ask FroggyAI reads at
             AA. White on the Froggy orange measured 3.1:1; the label is
             15px bold, under the large-text size, so it needs 4.5:1. It is
             now BLACK on Froggy Blue (8.8:1), the brand pair the chat
             panel's own header already wears, rather than a darker orange
             the operator ruled out for every orange element on 2026-10-09
             (the exact Froggy orange, white on it, or not orange). */
          background: var(--ff-brandBlue, #35c0e6);
          color: var(--ff-color-primary-contrast, #111111);
          border: none;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
          cursor: pointer;
          /* Was --z-popover (1080): "below header, notification and app
             modals so it doesn't cover Bootstrap dialogs" (adversarial
             review HIGH-2, 2026-05-25).

             Raised to the modal layer by the blanket-rule sweep
             (2026-09-04). NOTE this one is a real CHANGE, not a
             preservation: the button is not role=dialog, so it was never
             clamped and genuinely rendered at 1080. It moves because it
             is the panel's ONLY Close control and must share the panel's
             layer — at 1080 under a 1095 panel it could be buried while
             the panel it closes stayed visible. Nothing is lost to the
             original intent: "body.ff-modal-open" / "body.modal-open"
             hide the button outright when a dialog is open, so it still
             never covers one. */
          z-index: var(--z-modal, 1095);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
        }
        /* When ANY modal is open elsewhere in the app, hide the
           bubble + button entirely. The app modal-stack manager
           toggles body.ff-modal-open (or .modal-open from Bootstrap)
           and we hide on both so the bubble does not visually
           float over an attention-grabbing dialog. */
        body.ff-modal-open .froggy-bubble-btn,
        body.modal-open .froggy-bubble-btn,
        body.ff-modal-open .froggy-bubble-panel,
        body.modal-open .froggy-bubble-panel {
          display: none !important;
        }
        /* Side drawers get the same courtesy, with one difference.
           Operator, 2026-09-04: the bubble floated over the My Account
           drawer on a phone.

           Body classes: "drawer-account-open" is set by Navbar.js:31 for
           the My Account drawer; "fc-open" is set by useScrollLock
           (hooks/useFocusTrap.js:114), which FacilityConsoleDrawer.js:42
           and the racks phone sheet both use; "has-drawer" is the media
           library's ManageTagsDrawer (ManageTagsDrawer.js:79).

           ":not(.is-open)" is the difference: only the COLLAPSED bubble
           hides. If the panel is already open the operator is mid
           conversation, and the button is its only Close control — so an
           open chat is left alone rather than being torn down, or worse,
           stranded behind a drawer with no way to shut it.

           "visibility" rather than "display" so the reserved landing
           space below stays reserved, and with no transition added: this
           is an instant state change, so there is nothing for
           prefers-reduced-motion to object to. */
        body.drawer-account-open .froggy-bubble-btn:not(.is-open),
        body.fc-open .froggy-bubble-btn:not(.is-open),
        body.has-drawer .froggy-bubble-btn:not(.is-open) {
          visibility: hidden;
        }
        /* Cowork mobile sweep 2026-08-11: reserve the bubble's landing
           zone. A fixed decoration pushes nothing, so on phones it
           parked on whatever ended the page — including the cart's [+]
           stepper (32x28 of its 32x32 covered) and body copy on the
           homepage. While the bubble is mounted, the page gets bottom
           padding so the last content can always scroll clear of it.
           Lives in this style block deliberately: routes that don't
           mount the bubble don't pay the padding. */
        @media (max-width: 991.98px) {
          body { padding-bottom: 104px; }
        }
        /* THE FOOTER RESERVES THE PILL'S SPACE (run 39, batch 41): at the
           end of the scroll its link row sits above the pill. Desktop only
           - below 992 the body's 104px reserve above already does this job.
           Lives here for the same reason that rule does: a page without the
           bubble (the FroggerAI dock's pages) pays nothing. */
        @media (min-width: 992px) {
          .ff-footer, footer.app-footer {
            padding-bottom: calc(var(--froggy-pill-height, 0px) + 16px);
          }
        }
        .froggy-bubble-btn:hover { transform: scale(1.05); box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25); }
        .froggy-bubble-btn:focus-visible { outline: 3px solid #1565c0; outline-offset: 2px; }
        .froggy-bubble-btn-icon { font-size: 28px; line-height: 1; }
        .froggy-bubble-btn.is-open { background: var(--ff-brandBlue-hover, #2eadd1); /* the blue's own hover tone; black text 7.2:1 on the bold 15px label (R5 B5) */ }
        /* Frog mascot on the left of the pill. Wrapped in a forced
           white disc so the asset (which may have a transparent
           background) is guaranteed to render visibly on the
           pill. Operator screenshot 2026-05-26 showed the logo
           invisible when we relied on the asset's own background —
           moving to wrap+inset removes that variable. */
        .froggy-bubble-btn-logo-wrap {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
        }
        .froggy-bubble-btn-logo {
          width: 44px;
          height: 44px;
          object-fit: contain;
          display: block;
        }
        /* "FroggyAI" wordmark to the right of the mascot — what makes
           this distinct from the navbar's circular logo button. */
        .froggy-bubble-btn-label {
          font-weight: 700;
          font-size: 15px;
          letter-spacing: 0.2px;
          color: var(--ff-color-primary-contrast, #111111);
          white-space: nowrap;
        }

        .froggy-bubble-panel {
          position: fixed;
          bottom: 96px;
          right: 24px;
          width: 380px;
          max-width: calc(100vw - 32px);
          height: 600px;
          max-height: calc(100vh - 120px);
          background: white;
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
          display: flex;
          flex-direction: column;
          /* Pinned to the layer it has actually been RENDERING at.
             This element is role="dialog", so index.css's ARIA blanket
             (an attribute selector pinning every dialog to --z-modal
             with !important) had been overriding the --z-popover (1080)
             written here for as long as it existed — 1095 is what the
             panel has always been. The sweep (2026-09-04) deleted the
             blanket, which would have dropped the panel to 1080 for the
             first time: UNDER .facility-drawer-overlay (--z-scrim,
             1090), turning an already-open chat into a dimmed,
             untappable ghost. Preserve what rendered. */
          z-index: var(--z-modal, 1095);
          overflow: hidden;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }
        @media (max-width: 480px) {
          .froggy-bubble-panel {
            bottom: 16px;
            right: 16px;
            left: 16px;
            width: auto;
            max-width: none;
            height: calc(100vh - 96px);
          }
          /* On mobile the open panel covers the entire screen up to
             the button. Hide the button while open so the user
             doesn't tap behind the panel. Adversarial review MED-4. */
          .froggy-bubble-btn.is-open {
            display: none;
          }
        }

        .froggy-bubble-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          /* Froggy Blue with black text — operator brand direction
             2026-05-27. Green was the prior color but conflicted with
             the storefront primary. */
          background: var(--ff-brandBlue, #35c0e6);
          color: #000;
        }
        .froggy-bubble-title {
          font-weight: 600;
          font-size: 15px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .froggy-bubble-header-logo {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: white;
          padding: 2px;
          object-fit: cover;
        }
        .froggy-bubble-header-actions { display: flex; gap: 8px; }
        .froggy-bubble-link-btn {
          background: transparent;
          border: none;
          color: #000;
          font-size: 13px;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 4px;
        }
        .froggy-bubble-link-btn:hover { background: rgba(0, 0, 0, 0.08); }
        .froggy-bubble-link-btn:focus-visible { outline: 2px solid #000; outline-offset: 2px; }

        .froggy-bubble-messages {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          background: #fafafa;
          font-size: 14px;
          line-height: 1.45;
        }

        .froggy-bubble-welcome {
          color: #555;
          padding: 24px 8px;
          text-align: center;
        }
        .froggy-bubble-welcome p { margin: 8px 0; }

        .froggy-bubble-msg {
          margin: 12px 0;
          padding: 10px 14px;
          border-radius: 10px;
          max-width: 85%;
          word-wrap: break-word;
        }
        .froggy-bubble-msg--user {
          /* Froggy blue with BLACK text (operator ruling 2026-10-10): cyan
             never takes white text, it is under 2:1. */
          background: var(--ff-brand-primary, #67E8F9);
          color: var(--ff-text-on-primary, #111111);
          margin-left: auto;
        }
        .froggy-bubble-msg--assistant {
          background: white;
          color: #222;
          border: 1px solid #e0e0e0;
        }
        .froggy-bubble-msg--error {
          background: #fff3e0;
          color: #6d4c41;
          border: 1px solid #ffb300;
        }
        .froggy-bubble-msg--assistant p { margin: 0.5em 0; }
        .froggy-bubble-msg--assistant p:first-child { margin-top: 0; }
        .froggy-bubble-msg--assistant p:last-child { margin-bottom: 0; }

        .froggy-bubble-status {
          margin-top: 6px;
          font-size: 12px;
          color: #6c757d;
          font-style: italic;
        }

        .froggy-bubble-msg--assistant img.froggy-bubble-img {
          display: block;
          max-width: 100%;
          max-height: 200px;
          border-radius: 6px;
          margin: 6px 0;
          object-fit: cover;
        }

        .froggy-bubble-error {
          padding: 8px 16px;
          background: #ffebee;
          color: #b71c1c;
          font-size: 13px;
        }

        /* Action row above the input — Talk to a human + Voice. */
        .froggy-bubble-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px 0;
          background: white;
        }
        /* Talk to a human is NOT destructive and NOT the accent: it is the
           family SECONDARY (ff-btn ff-btn--secondary ff-btn--sm), driver
           ruling 2026-10-06. Its look comes from buttons.css; the orange
           fill that lived here is gone. (The Ask FroggyAI pill left the
           orange too in R5 B5: black on Froggy Blue.) */
        .froggy-bubble-voice-wrap {
          margin-left: auto;
        }
        /* Voice is the family SECONDARY here (variant="secondary"), at
           the sm height of the row it sits in, beside Talk to a human. */
        .froggy-bubble-voice-wrap .ff-btn.voice-recorder-btn {
          min-height: var(--ff-button-height-sm, 30px);
          padding: 0 10px;
          font-size: 13px;
        }

        .froggy-bubble-welcome-hint {
          margin-top: 8px;
          font-size: 12px;
          color: #6c757d;
        }

        /* Scope note — signals that FroggyAI knows more than just our
           catalog (handles care questions for any frog/amphibian/
           reptile, not just species we sell). Operator request
           2026-05-27 so customers don't self-filter their questions
           to "things they might buy from us." Soft inline treatment
           (italic + lightbulb prefix + muted color, no box) so it
           reads as a friendly aside rather than an alert callout. */
        .froggy-bubble-welcome-scope {
          margin-top: 6px;
          font-size: 12.5px;
          color: #555;
          line-height: 1.45;
        }

        /* Batch 34 C4: the composer is ONE height — the input and Send
           are both the family's 36px, centred on one line. Send's look
           is buttons.css (ff-btn ff-btn--primary): Froggy blue, #111
           text, 8px radius; disabled is the family's opacity .5, so the
           brand colour stays visible at rest. */
        .froggy-bubble-input {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px;
          background: white;
          border-top: 1px solid #e0e0e0;
        }
        .froggy-bubble-input input {
          flex: 1;
          box-sizing: border-box;
          height: var(--ff-button-height-md, 36px);
          padding: 0 12px;
          border: 1px solid var(--ff-border-strong, #bbb);
          border-radius: var(--ff-radius-lg, 8px);
          font-size: 14px;
        }
        .froggy-bubble-input input:focus {
          outline: 2px solid var(--ff-brandBlue, #35c0e6);
          outline-offset: 0;
          border-color: var(--ff-brandBlue, #35c0e6);
        }
        .froggy-bubble-input .froggy-bubble-send {
          flex: 0 0 auto;
        }
      `}</style>
    </>
  );
}
